import * as React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import PersonEmailToggleButton from 'vendors/vendorWizard/vendorContactInfoStep/components/PersonEmailToggleButton'
import UserContactForm from 'vendors/components/vendorContact/dialogs/UserContactForm'
import GeneralContactForm from 'vendors/components/vendorContact/dialogs/GeneralContactForm'
import { useAddVendorContact, useAddGeneralContact } from 'vendors/queries/useVendorQuery'
import { useUserContactForm } from 'vendors/components/vendorContact/useUserContactForm'
import { useGeneralContactForm } from 'vendors/components/vendorContact/useGeneralContactForm'
import LoadingButton from 'proposals/components/LoadingButton'
import { Navigate } from 'react-router-dom'
import { useSnackbar } from 'snackbar/SnackbarContext'
import { useTranslation } from 'react-i18next'

type AddVendorContactDialogProps = {
  isOpen: boolean
  onClose: () => void
  vendorId: string
  contacts?: VendorContactsResponseType
}

const AddVendorContactDialog: React.FC<AddVendorContactDialogProps> = ({ isOpen, onClose, vendorId, contacts }) => {
  const [contactType, setContactType] = React.useState<'person' | 'email'>('person')
  const { showSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const {
    mutate: addVendorContact,
    isPending: isUserLoading,
    isError: isUserError,
    isSuccess: isUserSuccess
  } = useAddVendorContact(vendorId)

  const {
    mutate: addGeneralContact,
    isPending: isGeneralLoading,
    isError: isGeneralError,
    isSuccess: isGeneralSuccess
  } = useAddGeneralContact(vendorId)

  const { vendorContactFormProps, vendorContactValues, email } = useUserContactForm(contacts)

  const { generalContactFormProps, generalEmail } = useGeneralContactForm(contacts)

  const isAddButtonDisabled = React.useMemo(() => {
    if (contactType === 'person') {
      return (
        vendorContactValues.firstName === '' ||
        vendorContactValues.lastName === '' ||
        email === '' ||
        contacts?.generalContacts.some((contact) => contact.email === email) ||
        contacts?.userContacts.some((contact) => contact.email === email)
      )
    }
    return (
      generalEmail === '' ||
      contacts?.generalContacts.some((contact) => contact.email === generalEmail) ||
      contacts?.userContacts.some((contact) => contact.email === generalEmail)
    )
  }, [contactType, generalEmail, vendorContactValues.firstName, vendorContactValues.lastName, email, contacts])

  React.useEffect(() => {
    if (isUserSuccess || isGeneralSuccess) {
      showSnackbar(t('contact-added-successfully'))
      onClose()
    }
  }, [isUserSuccess, isGeneralSuccess, onClose, showSnackbar, t])

  const handleContactTypeChange = (_event: React.MouseEvent<HTMLElement>, newContactType: 'person' | 'email') => {
    if (newContactType !== null) {
      setContactType(newContactType)
    }
  }

  const handleAddContactType = () => {
    if (contactType === 'person') {
      handleAddVendorContact()
    } else {
      handleAddGeneralContact()
    }
  }

  const handleAddVendorContact = () => {
    const payload = {
      email: email.toLowerCase(),
      ...vendorContactValues
    }
    addVendorContact(payload)
  }

  const handleAddGeneralContact = () => {
    const payload = {
      email: generalEmail.toLowerCase(),
      receiveRequestForProposals: true
    }
    addGeneralContact(payload)
  }

  if (isUserError || isGeneralError) {
    return <Navigate to="/error" />
  }

  return (
    <Dialog PaperProps={{ sx: { height: 615 } }} maxWidth="sm" fullWidth open={isOpen} onClose={onClose}>
      <DialogTitle>{t('add-contact')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <PersonEmailToggleButton value={contactType} onChange={handleContactTypeChange} />
        </Box>
        {contactType === 'person' && <UserContactForm {...vendorContactFormProps} />}
        {contactType === 'email' && <GeneralContactForm {...generalContactFormProps} />}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button color="grey" onClick={onClose}>
          {t('cancel')}
        </Button>
        <LoadingButton
          disabled={isAddButtonDisabled}
          onClick={handleAddContactType}
          isLoading={isUserLoading || isGeneralLoading}
        >
          {t('add')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default AddVendorContactDialog
